import { type FC, useEffect, useRef } from 'react';
import Input from '@components/Input';
import { Show, ShowFirstMatching } from '@components/Show';
import { SearchIcon } from '@heroicons/react/outline';
import type { SubmissionShort } from '@queries/fetchSubmissions';
import { useVirtualizer } from '@tanstack/react-virtual';

import { ContractListItem, ContractListLoadingSkeleton } from '../components';
import { LEFT_PANEL_HEIGHT_PX } from '../constants';

interface ContractsListOpenSearchProps {
  data: SubmissionShort[];
  isLoading: boolean;
  onAddContract: (contract: SubmissionShort) => void;
  onSearch: (value: string) => void;
  searchValue: string;
  hasNextPage: boolean;
  isFetchingNextPage: boolean;
  fetchNextPage: () => void;
}

export const ContractsListOpenSearch: FC<ContractsListOpenSearchProps> = ({
  data,
  isLoading,
  onAddContract,
  onSearch,
  searchValue,
  hasNextPage,
  isFetchingNextPage,
  fetchNextPage,
}) => {
  'use memo';
  const parentRef = useRef<HTMLDivElement>(null);
  const rowVirtualizer = useVirtualizer({
    count: hasNextPage ? data.length + 1 : data.length,
    getScrollElement: () => parentRef.current,
    estimateSize: () => 52,
    overscan: 5,
  });
  const virtualItems = rowVirtualizer.getVirtualItems();

  useEffect(() => {
    const [lastItem] = [...virtualItems].reverse();

    if (!lastItem) {
      return;
    }

    if (lastItem.index >= data.length - 1 && hasNextPage && !isFetchingNextPage) {
      fetchNextPage();
    }
  }, [hasNextPage, fetchNextPage, data.length, isFetchingNextPage, rowVirtualizer, virtualItems]);

  return (
    <div className="flex min-w-0 flex-1 flex-col">
      <div className="relative mb-2">
        <SearchIcon className="text-info-400 pointer-events-none absolute top-1/2 left-3 h-4 w-4 -translate-y-1/2" />
        <Input
          type="text"
          value={searchValue}
          disabled={isLoading}
          onChange={(event) => onSearch(event.currentTarget.value)}
          placeholder="Search contracts"
          className="border-info-300 focus:outline-primary-500 h-10 w-full rounded-md border py-2 pr-3 pl-9 text-sm leading-5 text-gray-700 shadow-xs"
        />
      </div>
      <div className="border-info-200 mt-4 border-t shadow-[0_5px_2px_-2px_rgba(0,0,0,0.06)]">
        <ShowFirstMatching>
          <Show when={isLoading}>
            <ContractListLoadingSkeleton />
          </Show>
          <Show when={!isLoading}>
            <div
              ref={parentRef}
              className="w-full overflow-auto"
              data-testid="contracts-list"
              style={{
                height: `${LEFT_PANEL_HEIGHT_PX}px`,
              }}
            >
              <div
                className="relative w-full"
                style={{
                  height: `${rowVirtualizer.getTotalSize()}px`,
                }}
              >
                {rowVirtualizer.getVirtualItems().map((virtualRow) => {
                  const isLoaderRow = virtualRow.index > data.length - 1;
                  const contract = data[virtualRow.index];

                  return (
                    <div
                      key={virtualRow.index}
                      data-testid={`contract-list-item-${virtualRow.index}`}
                      className="absolute top-0 left-0 w-full"
                      style={{
                        height: `${virtualRow.size}px`,
                        transform: `translateY(${virtualRow.start}px)`,
                      }}
                    >
                      {isLoaderRow ? (
                        <div className="truncate px-3 py-2 text-sm leading-5 font-normal transition-colors">
                          {hasNextPage ? 'Loading more...' : 'Nothing more to load'}
                        </div>
                      ) : (
                        <ContractListItem contract={contract} onAdd={onAddContract} />
                      )}
                    </div>
                  );
                })}
              </div>
            </div>
          </Show>
        </ShowFirstMatching>
      </div>
    </div>
  );
};
